// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BundleMemberFormValue } from '@/bundle/schemas/bundle-members';
import { NumberTextField } from '@/components/number-text-field';
import {
  DeleteRounded,
  DragIndicatorRounded,
} from '@/constants/hardfin-standard-icons';
import { useStableValue } from '@/hooks/use-stable-value';
import { ItemSearch } from '@/items/components/item-search';
import { SparseItem } from '@/sparse/api/types';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import {
  MutableRefObject,
  Ref,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  FieldValues,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

const BUNDLE_MEMBER_DND_TYPE = 'bundle-member-row';

function makeBundleMemberDragData(index: number): Record<string, unknown> {
  return { type: BUNDLE_MEMBER_DND_TYPE, index };
}

function isBundleMemberDragData(
  data: Record<string, unknown>
): data is Record<string, unknown> & { type: string; index: number } {
  return data.type === BUNDLE_MEMBER_DND_TYPE;
}

interface RowProps {
  name: string;
  index: number;
  disabled: boolean;
  isOnlyRow: boolean;
  selectedItems: SparseItem[];
  dragSourceIndexRef: MutableRefObject<number | null>;
  onSwap: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
}

/**
 * One editable member row: drag handle, item picker, default quantity,
 * and a remove button. Memoized so rows skip re-rendering when their
 * props are unchanged; selectedItems keeps a stable identity upstream
 * so sibling keystrokes do not defeat the memo.
 */
const BundleMemberRow = memo(function BundleMemberRow(props: RowProps) {
  const {
    name,
    index,
    disabled,
    isOnlyRow,
    dragSourceIndexRef,
    onSwap,
    onRemove,
  } = props;
  const { control } = useFormContext<FieldValues>();
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const row = rowRef.current;
    const handle = handleRef.current;
    if (!row || !handle || disabled) {
      return;
    }

    const dragData = makeBundleMemberDragData(index);

    // The drag source is the HANDLE only, not the row — making the row
    // draggable would break text selection inside the row's inputs.
    const cleanupDraggable = draggable({
      element: handle,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
      getInitialData: () => dragData,
    });

    // The row remains the drop target so swaps trigger as the user drags
    // the handle over any part of another row.
    const cleanupDropTarget = dropTargetForElements({
      element: row,
      getData: () => dragData,
      canDrop: () => {
        const fromIndex = dragSourceIndexRef.current;
        return fromIndex !== null && fromIndex !== index;
      },
      onDragEnter: () => {
        const fromIndex = dragSourceIndexRef.current;
        if (fromIndex === null || fromIndex === index) return;
        onSwap(fromIndex, index);
        dragSourceIndexRef.current = index;
      },
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [index, disabled, onSwap, dragSourceIndexRef]);

  return (
    <Stack
      ref={rowRef}
      direction="row"
      spacing={2}
      alignItems="center"
      width="100%"
      sx={{ opacity: isDragging ? 0.65 : 1 }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip arrow title="Drag to reorder">
          <span>
            <IconButton
              ref={handleRef}
              size="small"
              disabled={disabled}
              aria-label="Drag to reorder"
              sx={{ cursor: 'grab' }}
            >
              <DragIndicatorRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {/* The item picker takes all row width the fixed columns leave over */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Controller
          control={control}
          name={`${name}.${index}.item`}
          render={({ field: itemField, fieldState }) => (
            <ItemSearch
              label="Item"
              placeholderText="Select an item"
              value={itemField.value === undefined ? null : itemField.value}
              onChange={(value) => {
                itemField.onChange(value);
              }}
              disabled={disabled}
              includeArchived
              hiddenItems={props.selectedItems.filter(
                (selected) => selected.id !== itemField.value?.id
              )}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>
      <Box sx={{ width: '9rem', flexShrink: 0 }}>
        <Controller
          control={control}
          name={`${name}.${index}.quantity`}
          render={({ field: quantityField, fieldState }) => (
            <NumberTextField
              label="Default quantity"
              value={quantityField.value}
              onChange={(values) => {
                quantityField.onChange(
                  values.value === '' ? null : values.floatValue
                );
              }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabled}
              allowZero={false}
              size="small"
              fullWidth
            />
          )}
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip
          arrow
          title={isOnlyRow ? 'Bundle must have at least one item' : ''}
        >
          <span>
            <IconButton
              onClick={() => onRemove(index)}
              disabled={disabled || isOnlyRow}
              size="small"
              aria-label="Remove item"
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
});

/**
 * Props for BundleMembersForm.
 * The component uses useFormContext to get the form control,
 * so it must be wrapped in a FormProvider.
 */
interface Props {
  /**
   * The name of the members field in the form.
   * The field at this path should be an array of BundleMemberFormValue objects.
   */
  name: string;

  /**
   * Whether the form is disabled.
   */
  disabled?: boolean;
}

export interface BundleMembersFormRef {
  /**
   * Appends a new empty member row to the form.
   */
  addMemberRow: () => void;
}

/**
 * Inner implementation of BundleMembersForm.
 * Uses useFormContext to get the form control from React context.
 */
function BundleMembersFormInner(props: Props, ref: Ref<BundleMembersFormRef>) {
  const { name, disabled = false } = props;

  const { control } = useFormContext<FieldValues>();

  // Watch the members field to hide already-selected items from pickers
  const watchedValue = useWatch({ control, name });
  const members: BundleMemberFormValue[] = Array.isArray(watchedValue)
    ? watchedValue
    : [];
  // Keep the array identity stable while the selection is unchanged so
  // quantity keystrokes do not defeat the rows' memoization
  const selectedItems = useStableValue(
    members
      .map((member: BundleMemberFormValue) => member.item)
      .filter((item: SparseItem | null): item is SparseItem => item !== null),
    (previous, next) =>
      previous.length === next.length &&
      previous.every((item, index) => item.id === next[index].id)
  );

  const { fields, remove, append, move } = useFieldArray({ control, name });

  // Tracks the in-flight drag source index across rows; a ref (not state)
  // because it must mutate during the drag without re-rendering rows
  const dragSourceIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (disabled) {
      return;
    }
    return monitorForElements({
      onDragStart: ({ source }) => {
        const sourceData = source.data;
        if (isBundleMemberDragData(sourceData)) {
          dragSourceIndexRef.current = sourceData.index;
        }
      },
      onDrop: () => {
        dragSourceIndexRef.current = null;
      },
    });
  }, [disabled]);

  useImperativeHandle(ref, () => ({
    addMemberRow: () => {
      append({ item: null, quantity: 1 });
    },
  }));

  return (
    <Stack spacing={2} marginY={1}>
      {fields.map((field, index) => (
        <BundleMemberRow
          key={field.id}
          name={name}
          index={index}
          disabled={disabled}
          isOnlyRow={fields.length === 1}
          selectedItems={selectedItems}
          dragSourceIndexRef={dragSourceIndexRef}
          onSwap={move}
          onRemove={remove}
        />
      ))}
    </Stack>
  );
}

/**
 * The shared members form for bundle authoring. One component serves both
 * the create dialog and the detail page's Items section.
 *
 * IMPORTANT: This component must be wrapped in a FormProvider from
 * react-hook-form. It uses useFormContext internally.
 *
 * Features:
 * - Dynamic addition and removal of member rows
 * - Item selection via the shared ItemSearch (any item type; archived
 *   items remain selectable; already-selected items hidden)
 * - Positive integer default quantity per member
 * - Drag-and-drop reordering via a dedicated handle (member order drives
 *   line append order on apply)
 */
export const BundleMembersForm = forwardRef(BundleMembersFormInner);
